﻿import $ from 'jquery';
import Element from '../utilities/element';
import _ from 'underscore';

declare global {

  interface JQuery {
    searchField(options?: any, args?: any): JQuery;
  }
}

export class SearchField {
  private static id: number = 0;
  static get wireup(): string {
    return '[data-searchfield]';
  }
  private debouncedDoAction: Function;
  private $element: JQuery;
  private $searchForm: JQuery;
  private isPartOfSearchForm: boolean;
  private $targetForm: JQuery;
  private $dataForm: JQuery;
  private hasTargetForm: boolean;
  private hasCustomAction: boolean;
  private remoteDataFilter: (data: any, state: any) => any;
  private $filterElement: JQuery;
  private get filterId(): number {
    return this.$filterElement.data('rustig.searchfield.filterId') || 0;
  }
  private set filterId(value: number) {
    this.$filterElement.data('rustig.searchfield.filterId', value);
  }
  private remoteAction: Function;
  private element: Element;
  private value: any;

  private DEFAULTS: ISearchFieldOptions = {
    sfDelay: 500
  };

  constructor(element: JQuery, private options?: ISearchFieldOptions) {
    this.$element = $(element);
    this.init();
  }

  public setValue(value: any) {
    this.value = value;
  }

  private init(): void {
    this.options = $.extend({}, this.DEFAULTS, this.$element.data(), this.options);
    this.isPartOfSearchForm = !this.options.url;
    this.hasCustomAction = this.options.action != null;
    this.resolveTargetForm();
    this.$searchForm = this.hasTargetForm ? this.$targetForm : this.$element.closest('form');
    if (!this.hasCustomAction) {
      this.remoteDataFilter = this.getDefaultDataFilter();
      this.$filterElement = this.isPartOfSearchForm ? this.$searchForm : this.$element;
    }

    this.element = new Element(this.$element);
    this.value = this.getsCurrentValue();

    this.validate();

    var actionEventName = this.determineEventName();

    if (this.isPartOfSearchForm && !this.hasCustomAction) {
      let formDebounceFunction = this.$filterElement.data('rustig.searchfield.debounce');
      if (!formDebounceFunction) {
        formDebounceFunction = _.debounce((e) => this.doAction(e), this.options.sfDelay);
        this.$filterElement.data('rustig.searchfield.debounce', formDebounceFunction);
      }
      this.debouncedDoAction = formDebounceFunction;
    }
    else {
      this.debouncedDoAction = _.debounce((e) => this.doAction(e), this.options.sfDelay);
    }

    this.$element.on(actionEventName, (e) => this.debouncedDoAction(e));
  }

  private doAction(e: JQueryEventObject): void {
    if (!this.valueChanged())
      return;

    if (this.options.action) {
      this.options.action(this.$element, e);
      return;
    }

    var data = {};
    if (this.hasTargetForm) {
      var $formInputs = this.$dataForm.find('[name]');
      var $this = this;
      $formInputs.each(function () {
        $this.addInputToData($(this), data);
      });
    }
    else if (!this.isPartOfSearchForm) {
      this.addInputToData(this.$filterElement, data);
    }
    else {
      this.addsNameOverridesToFormData(data);
    }

    this.$filterElement.remote({
      state: ++this.filterId,
      data: data,
      dataFilter: this.remoteDataFilter,
      allowMultipleAjaxCalls: true
    });
  }

  private addsNameOverridesToFormData(data: any) {
    let $searchfieldNameInputs = this.$filterElement.find('[data-searchfield-name]').addBack();
    $searchfieldNameInputs.each((i, e) => {
      this.addInputToData($(e), data);
    });
  }

  private valueChanged(): boolean {
    var currentValue = this.getsCurrentValue();

    if (this.value === currentValue)
      return false;

    this.value = currentValue;
    return true;
  }

  private getsCurrentValue(): any {
    if (this.isPartOfSearchForm && !this.hasCustomAction) {
      let nameSorter = (a: any, b: any) => {
        if (a.name > b.name)
          return 1;
        if (b.name > a.name)
          return -1;
        return 0;
      }
      let joinReducer = (output: string, currentValue: any) => output + "&" + currentValue.name + "=" + currentValue.value;
      let selector = this.element.element.matches('form') ? '[name]' : SearchField.wireup;
      return this.$filterElement.find(selector)
        .toArray()
        .map((element: HTMLElement) => Element.from(element))
        .map((element: Element) => { return { name: element.name, value: element.value() } })
        .sort(nameSorter)
        .reduce(joinReducer, '');
    }
    else
      return this.element.value();
  }

  private validate(): void {
    if (this.hasCustomAction)
      return;

    if (this.isPartOfSearchForm && this.$searchForm.length == 0)
      throw new Error('The search field is part of a search form and the form cannot be found.');

    if (this.hasTargetForm && this.$targetForm.length == 0)
      throw new Error('the target form for the search fields could not be found.');
  }

  private getDefaultDataFilter(): any {
    return (data, state) => {
      if (state == this.filterId) {
        return data;
      }
      return null;
    }
  }

  private addInputToData($input: JQuery, data) {
    let element = new Element($input);
    let name = $input.data('searchfield-name') || element.name();
    data[name] = element.value();
  }

  private resolveTargetForm() {
    this.$dataForm = this.$element.closest('[data-form]');
    var targetFormSelector = this.$dataForm.data('form');
    if (targetFormSelector) {
      this.hasTargetForm = true;
      this.$targetForm = $(targetFormSelector);
    }
  }

  private determineEventName(): string {
    var actions: Array<string> = new Array<string>('rustig.valuebinding.valueset');

    if (this.options.sfTrigger)
      actions.push(this.options.sfTrigger);
    else if (this.$element.is('form')) {
      actions.push('change.rustig');
      actions.push('change.rustig.searchfield');
      actions.push('input.rustig.searchfield');
    }
    else {
      var isDateField = this.$element.is('[data-datefield]');
      var triggersFromChangeEvent = this.$element.is('select, [type="checkbox"], .input-radio-group, [data-ms-real-input], [data-daterange-input]');

      if (isDateField)
        actions.push('change.rustig');
      else if (triggersFromChangeEvent)
        actions.push('change.rustig.searchfield');
      else
        actions.push('input.rustig.searchfield');
    }

    return actions.join(' ');
  }
}

export interface ISearchFieldOptions {
  url?: string;
  action?: ($element: JQuery, event: JQueryEventObject) => void;
  sfDelay?: number;
  sfTrigger?: string;
}

(function ($) {
  function Plugin(option, args) {
    return this.each(function () {
      var $this = $(this);
      var data = $this.data('rustig.searchfield');
      var options = typeof option == 'object' && option;

      if (!data) {
        data = new SearchField(this, options);
        $this.data('rustig.searchfield', data);
      }
      if (typeof option == 'string') {
        data[option](args);
      }
    });
  }
  (<any>$.fn).searchField = Plugin;
})($);